import React, { useState, useEffect } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import toast from "react-hot-toast";
import Swal from "sweetalert2";

const UpdateSetModal = ({
  isOpen,
  onClose,
  set,
  onUpdate,
  handleDeleteSet,
}) => {
  const [setTitle, setSetTitle] = useState(set?.setTitle || "");
  const [startDate, setStartDate] = useState(
    set?.startDate ? new Date(set.startDate) : null
  );
  const [endDate, setEndDate] = useState(
    set?.endDate ? new Date(set.endDate) : null
  );

  // State for time selection
  const [startTime, setStartTime] = useState({
    hour: "12",
    minute: "00",
    period: "AM",
  });
  const [endTime, setEndTime] = useState({
    hour: "12",
    minute: "00",
    period: "AM",
  });

  // Sync state when `set` prop updates
  useEffect(() => {
    if (set) {
      setSetTitle(set.setTitle || "");
      const startDateObj = set.startDate ? new Date(set.startDate) : null;
      const endDateObj = set.endDate ? new Date(set.endDate) : null;

      setStartDate(startDateObj);
      setEndDate(endDateObj);

      if (startDateObj) {
        const startHours = startDateObj.getUTCHours(); // Use getUTCHours() here
        const startMinutes = startDateObj.getUTCMinutes(); // Use getUTCMinutes() here
        setStartTime({
          hour: startHours % 12 === 0 ? "12" : String(startHours % 12),
          minute: String(startMinutes).padStart(2, "0"),
          period: startHours >= 12 ? "PM" : "AM",
        });
      }

      if (endDateObj) {
        const endHours = endDateObj.getUTCHours(); // Use getUTCHours() here
        const endMinutes = endDateObj.getUTCMinutes(); // Use getUTCMinutes() here
        setEndTime({
          hour: endHours % 12 === 0 ? "12" : String(endHours % 12),
          minute: String(endMinutes).padStart(2, "0"),
          period: endHours >= 12 ? "PM" : "AM",
        });
      }
    }
  }, [set]);

  // Function to combine date and time without timezone conversion
  const combineDateAndTime = (date, time) => {
    const newDate = new Date(date);
    let hours = parseInt(time.hour, 10);
    const minutes = parseInt(time.minute, 10);

    // Convert to 24-hour format
    if (time.period === "PM" && hours !== 12) hours += 12;
    if (time.period === "AM" && hours === 12) hours = 0;

    // Create a new date in UTC to avoid timezone issues
    const utcDate = new Date(
      Date.UTC(
        newDate.getFullYear(),
        newDate.getMonth(),
        newDate.getDate(),
        hours,
        minutes,
        0
      )
    );

    return utcDate;
  };

  // Function to format date as ISO string without timezone conversion
  const toLocalISOString = (date) => {
    const pad = (n) => (n < 10 ? "0" + n : n);
    return (
      date.getUTCFullYear() +
      "-" +
      pad(date.getUTCMonth() + 1) +
      "-" +
      pad(date.getUTCDate()) +
      "T" +
      pad(date.getUTCHours()) +
      ":" +
      pad(date.getUTCMinutes()) +
      ":" +
      pad(date.getUTCSeconds()) +
      ".000Z"
    );
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!setTitle || !startDate || !endDate) {
      toast.error("Please provide a title, start date, and end date.");
      return;
    }

    // Combine date and time for start and end dates
    const startDateTime = combineDateAndTime(startDate, startTime);
    const endDateTime = combineDateAndTime(endDate, endTime);

    // Validate that end date/time is after start date/time
    if (endDateTime <= startDateTime) {
      toast.error("End date/time must be after start date/time");
      return;
    }

    // Send updated values with properly formatted dates
    onUpdate(
      set.setCode,
      {
        setTitle,
        startDate: toLocalISOString(startDateTime),
        endDate: toLocalISOString(endDateTime),
      },
      set._id
    );

    onClose(); // Close modal
  };

  const handleDelete = async () => {
    handleDeleteSet(set.setCode);
    onClose();
  };

  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 flex items-center justify-center">
      <div className="bg-gray-100 rounded-2xl w-full max-w-lg shadow-xl transform transition-all duration-300 scale-100">
        {/* Modal Header */}
        <div className="flex rounded-t-2xl justify-between py-3 px-5 items-center border-b shadow-md bg-blue-600">
          <h1 className="text-xl font-semibold text-white">Update Set</h1>
          <button
            onClick={onClose}
            aria-label="Close modal"
            className="w-9 h-9 rounded-full flex justify-center items-center transition hover:bg-blue-700"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-7 w-7 text-white"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <div className="space-y-5 p-6">
            {/* Set Title Input */}
            <div>
              <label className="block font-roboto text-md text-gray-900 mb-2">
                Set Title
              </label>
              <input
                type="text"
                value={setTitle}
                onChange={(e) => setSetTitle(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all duration-200"
                required
              />
            </div>

            {/* Start Date and Time */}
            <div>
              <label className="block font-roboto text-md text-gray-900 mb-2">
                Start Date
              </label>
              <DatePicker
                selected={startDate}
                onChange={(date) => setStartDate(date)}
                placeholderText="Select Start Date"
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all duration-200"
              />
              <div className="flex space-x-2 mt-2">
                <select
                  value={startTime.hour}
                  onChange={(e) =>
                    setStartTime({ ...startTime, hour: e.target.value })
                  }
                  className="border border-gray-300 p-2 rounded-lg"
                >
                  {Array.from({ length: 12 }, (_, i) => (
                    <option key={i + 1} value={i + 1}>
                      {i + 1}
                    </option>
                  ))}
                </select>
                <select
                  value={startTime.minute}
                  onChange={(e) =>
                    setStartTime({ ...startTime, minute: e.target.value })
                  }
                  className="border border-gray-300 p-2 rounded-lg"
                >
                  {Array.from({ length: 60 }, (_, i) => (
                    <option key={i} value={i < 10 ? `0${i}` : i}>
                      {i < 10 ? `0${i}` : i}
                    </option>
                  ))}
                </select>
                <select
                  value={startTime.period}
                  onChange={(e) =>
                    setStartTime({ ...startTime, period: e.target.value })
                  }
                  className="border border-gray-300 p-2 rounded-lg"
                >
                  <option value="AM">AM</option>
                  <option value="PM">PM</option>
                </select>
              </div>
            </div>

            {/* End Date and Time */}
            <div>
              <label className="block font-roboto text-md text-gray-900 mb-2">
                End Date
              </label>
              <DatePicker
                selected={endDate}
                onChange={(date) => setEndDate(date)}
                placeholderText="Select End Date"
                minDate={startDate} // Set minDate to startDate
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all duration-200"
              />
              <div className="flex space-x-2 mt-2">
                <select
                  value={endTime.hour}
                  onChange={(e) =>
                    setEndTime({ ...endTime, hour: e.target.value })
                  }
                  className="border border-gray-300 p-2 rounded-lg"
                >
                  {Array.from({ length: 12 }, (_, i) => (
                    <option key={i + 1} value={i + 1}>
                      {i + 1}
                    </option>
                  ))}
                </select>
                <select
                  value={endTime.minute}
                  onChange={(e) =>
                    setEndTime({ ...endTime, minute: e.target.value })
                  }
                  className="border border-gray-300 p-2 rounded-lg"
                >
                  {Array.from({ length: 60 }, (_, i) => (
                    <option key={i} value={i < 10 ? `0${i}` : i}>
                      {i < 10 ? `0${i}` : i}
                    </option>
                  ))}
                </select>
                <select
                  value={endTime.period}
                  onChange={(e) =>
                    setEndTime({ ...endTime, period: e.target.value })
                  }
                  className="border border-gray-300 p-2 rounded-lg"
                >
                  <option value="AM">AM</option>
                  <option value="PM">PM</option>
                </select>
              </div>
            </div>
          </div>

          {/* Buttons */}
          <div className="p-6 flex justify-between bg-gray-50 border-t border-gray-200">
            <button
              type="submit"
              className="py-3 px-6 font-roboto bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg focus:ring-4 focus:outline-none focus:ring-blue-300 transition-all"
            >
              Update Set
            </button>
            <button
              type="button"
              onClick={handleDelete}
              className="py-3 px-6 font-roboto text-red-500 underline  rounded-lg focus:ring-4 focus:outline-none focus:ring-red-300 transition-all"
            >
              Delete Set
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UpdateSetModal;
